import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authAPI } from '../api/auth';
import { authStore } from '../store/authStore';
import { LoginRequest, RegisterRequest, AuthResponse } from '../types/auth';

/**
 * Custom hook for authentication
 * Provides login, logout, register, and user fetching functionality
 */
export const useAuth = () => {
  const queryClient = useQueryClient();
  const { setTokens, setUser, logout: logoutStore, loadFromStorage } = authStore();
  const { accessToken, refreshToken, user, isAuthenticated } = authStore();

  /**
   * Login mutation
   */
  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const response = await authAPI.login(credentials);
      return response.data;
    },
    onSuccess: (data: AuthResponse) => {
      // Clear old user data first to prevent mixups when switching profiles
      queryClient.removeQueries({ queryKey: ['currentUser'] });
      queryClient.clear();
      localStorage.removeItem('user');
      setTokens(data);
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
    onError: (error: any) => {
      console.error('Login failed:', error);
    },
  });

  /**
   * Register mutation
   */
  const registerMutation = useMutation({
    mutationFn: async (data: RegisterRequest) => {
      const response = await authAPI.register(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      console.error('Registration failed:', error);
    },
  });

  /**
   * Get current user query
   */
  const currentUserQuery = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const response = await authAPI.getCurrentUser();
      return response.data;
    },
    enabled: isAuthenticated && !!accessToken,
    staleTime: 0, // Always refetch on mount
    gcTime: 1000 * 60 * 5, // 5 minutes cache
    // A killed session (evicted elsewhere by the 2-device cap, or an
    // expired refresh token) is otherwise only ever discovered on the next
    // fresh mount of a PrivateRoute-wrapped page -- a tab left open on an
    // already-rendered dashboard has nothing that would notice, since the
    // app-wide QueryClient default disables refetchOnWindowFocus. Overriding
    // it here (just for this one check) means coming back to the tab
    // re-verifies the session instead of trusting stale local state
    // indefinitely; the interval is a belt-and-suspenders backstop for a tab
    // that's never blurred, timed to the access token's own 5-minute
    // lifetime so a dead session is caught within one token cycle either way.
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 60 * 5,
  });

  // Set user whenever a fresh fetch comes back, not just on account switch -
  // fields like has_accepted_current_terms/is_verified can change server-side
  // between sessions and need to reach the cached store object.
  useEffect(() => {
    if (currentUserQuery.data) {
      setUser(currentUserQuery.data);
    }
  }, [currentUserQuery.data, setUser]);

  // Handle 401 error
  useEffect(() => {
    if (currentUserQuery.isError && (currentUserQuery.error as any)?.response?.status === 401) {
      logoutStore();
    }
  }, [currentUserQuery.isError, currentUserQuery.error, logoutStore]);

  /**
   * Logout function
   */
  const logout = () => {
    logoutStore();
    queryClient.removeQueries({ queryKey: ['currentUser'] });
    queryClient.clear();
  };

  return {
    // State
    user: user || currentUserQuery.data,
    accessToken,
    refreshToken,
    isAuthenticated,
    isLoading: loginMutation.isPending || registerMutation.isPending,
    currentUserLoading: currentUserQuery.isLoading,
    error: loginMutation.error || registerMutation.error,

    // Methods
    login: loginMutation.mutate,
    register: registerMutation.mutateAsync,
    logout,
    refetchUser: currentUserQuery.refetch,
    loadFromStorage,
  };
};
